import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PrescriptionPDFDocument } from '../pdfService';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import BaseDialog from '../../../components/Dialogs/BaseDialog';
import colorContrast from 'color-contrast';
import { isProfessionalIssuerDoctor } from '../../../helpers/string';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';

interface DialogPDFPreviewProps {
  documents: Array<PrescriptionPDFDocument>;
  onClose(): void;
}

export const tabIndicator = {
  border: '1px solid #3484FA',
  backgroundColor: '#fff',
  boxShadow: '4px 4px 10px 0px rgba(0, 0, 0, 0.1)',
};

interface StyledTabProps {
  label: string;
  color: string;
}

const StyledTab = (props: StyledTabProps) => {
  return (
    <Tab
      height={'30px'}
      _selected={{
        backgroundColor: 'primary.main',
        boxShadow: 'none',
        color: colorContrast('#fff', props.color) > 3 ? 'white' : '#1E252B',
      }}
      lineHeight={'13px'}
      fontSize={'13px'}
    >
      <Box>{props.label}</Box>
    </Tab>
  );
};

const decodePdfBase64 = (pdfBase64: string) => {
  const sanitizedBase64 = pdfBase64.replace(/^data:application\/pdf;base64,/, '');
  const pdfBinary = window.atob(sanitizedBase64);

  return Uint8Array.from(pdfBinary, (character) => character.charCodeAt(0));
};

let pdfJsModulePromise: Promise<typeof import('pdfjs-dist/legacy/build/pdf')> | null = null;

const loadPdfJsModule = async () => {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise = import('pdfjs-dist/legacy/build/pdf');
  }

  try {
    const pdfJsModule = await pdfJsModulePromise;
    pdfJsModule.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

    return pdfJsModule;
  } catch (error) {
    pdfJsModulePromise = null;
    throw error;
  }
};

interface PreviewDocumentWithKey extends PrescriptionPDFDocument {
  key: string;
}

interface HtmlPreviewProps {
  htmlDocuments: string[];
  previewKey: string;
  onLoad(): void;
}

const HtmlPreview = (props: HtmlPreviewProps) => {
  const { htmlDocuments, previewKey, onLoad } = props;

  return (
    <>
      {htmlDocuments.map((html, htmlIndex) => (
        <iframe
          srcDoc={html}
          title={`preview-${previewKey}-${htmlIndex}`}
          style={{
            width: '210mm',
            height: '297mm',
            background: 'white',
            border: 'none',
            display: 'block',
            margin: '0 auto 15px',
            padding: '20px',
          }}
          onLoad={onLoad}
        ></iframe>
      ))}
    </>
  );
};

interface PdfCanvasPreviewProps {
  document: PreviewDocumentWithKey;
  onLoad(): void;
}

const PdfCanvasPreview = (props: PdfCanvasPreviewProps) => {
  const { document, onLoad } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const container = containerRef.current;
    let loadingTask: { promise: Promise<any>; destroy(): void } | null = null;

    const renderPdf = async () => {
      if (!document.pdfBase64 || !container) return;

      setRenderFailed(false);

      try {
        const pdfJsModule = await loadPdfJsModule();

        if (isCancelled) return;

        loadingTask = pdfJsModule.getDocument({ data: decodePdfBase64(document.pdfBase64) });
        const pdfDocument = await loadingTask.promise;

        if (!container || isCancelled) return;

        container.innerHTML = '';

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          if (isCancelled) return;

          const page = await pdfDocument.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1.25 });
          const outputScale = window.devicePixelRatio || 1;
          const canvas = window.document.createElement('canvas');
          const canvasContext = canvas.getContext('2d');

          if (!canvasContext) {
            throw new Error('Canvas context unavailable');
          }

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          canvas.style.display = 'block';
          canvas.style.background = '#fff';
          canvas.style.margin = '0 auto';

          canvasContext.setTransform(outputScale, 0, 0, outputScale, 0, 0);

          const pageWrapper = window.document.createElement('div');
          pageWrapper.style.width = 'fit-content';
          pageWrapper.style.margin = '0 auto 15px';
          pageWrapper.style.padding = '20px';
          pageWrapper.style.background = '#fff';

          pageWrapper.appendChild(canvas);
          container.appendChild(pageWrapper);

          await page.render({
            canvasContext,
            viewport,
          }).promise;
        }

        if (!isCancelled) {
          onLoad();
        }
      } catch (error) {
        if (!isCancelled) {
          setRenderFailed(true);
          onLoad();
        }
      }
    };

    renderPdf();

    return () => {
      isCancelled = true;

      if (container) {
        container.innerHTML = '';
      }

      loadingTask?.destroy();
    };
  }, [document, onLoad]);

  if (renderFailed && document.html.length > 0) {
    return <HtmlPreview htmlDocuments={document.html} previewKey={document.key} onLoad={onLoad} />;
  }

  if (renderFailed) {
    return (
      <Box
        aria-label={`pdf-render-error-${document.key}`}
        minHeight={'100%'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        backgroundColor={'white'}
        color={'#495057'}
        padding={'24px'}
      >
        Não foi possível carregar a prévia do PDF.
      </Box>
    );
  }

  return <div ref={containerRef} aria-label={`pdf-canvas-preview-${document.key}`} style={{ minHeight: '100%' }} />;
};

export function DialogPDFPreview(props: DialogPDFPreviewProps) {
  const { documents } = props;
  const [loading, setLoading] = useState(true);
  const primaryColor = usePrescriptionStore((store) => store.primaryColor());

  const prescriber = usePrescriptionStore((store) => store.prescriber());

  const canShowMedicalPrescription = isProfessionalIssuerDoctor(prescriber?.Conselho || '');

  const TYPE_LABEL = {
    MEDICINE: canShowMedicalPrescription ? 'Receita médica' : 'Receita',
    EXAM: 'Exame',
    MEDICAL_STATEMENT: 'Atestado',
    SMR: 'Formulário LME',
    REFERRAL: 'Encaminhamento',
    MEDICAL_REPORT: 'Relatório',
  };

  const hasMoreThanOneDocument = useMemo(() => {
    return documents.length > 1;
  }, [documents]);

  const previewDocuments = useMemo(
    () =>
      documents.map((document, index) => ({
        ...document,
        key: `${document.type}-${index}`,
      })),
    [documents]
  );

  return (
    <BaseDialog
      isOpen={true}
      onClose={props.onClose}
      stylesCustom={{
        minWidth: '80vw',
        height: '80vh',
      }}
    >
      <Tabs variant="soft-rounded" isFitted w={'100%'} h={'100%'}>
        <TabPanels h={hasMoreThanOneDocument ? 'calc(100% - 40px - 10px)' : '100%'}>
          {previewDocuments.map((item) => {
            return (
              <TabPanel h={'100%'} key={item.key} bg={`#3d3d3d`} p={'35px 20px'} overflow={'auto'}>
                {loading && (
                  <Box
                    position={'absolute'}
                    top={0}
                    right={0}
                    bottom={0}
                    left={0}
                    bg={'#fff'}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    color={'#ADB5BD'}
                  >
                    <Spinner size={'md'} />
                  </Box>
                )}

                {item.pdfBase64 ? (
                  <PdfCanvasPreview
                    document={item}
                    onLoad={() => {
                      setLoading(false);
                    }}
                  />
                ) : (
                  <HtmlPreview
                    htmlDocuments={item.html}
                    previewKey={item.key}
                    onLoad={() => {
                      setLoading(false);
                    }}
                  />
                )}
              </TabPanel>
            );
          })}
        </TabPanels>

        {hasMoreThanOneDocument && (
          <TabList
            p={'8px'}
            h={'40px'}
            bg={'gray.lightest'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            borderRadius={'30px'}
            mt={'10px'}
          >
            {previewDocuments.map((item) => {
              return (
                <StyledTab
                  key={item.key}
                  label={TYPE_LABEL[item.type as keyof typeof TYPE_LABEL]}
                  color={primaryColor}
                />
              );
            })}
          </TabList>
        )}
      </Tabs>
    </BaseDialog>
  );
}
